import type {Component, OnChanges, OnChangesObject} from 'typings/portaro.fe.types';
import type {IAugmentedJQuery, IOnDestroy, IOnInit} from 'angular';
import 'video.js/dist/video-js.min.css';
import type {VideoJsPlayer} from 'video.js';
import type {KpVideoViewerEventWrapperComponentController} from './kp-video-viewer-event-wrapper.component';
import KpVideoViewerEventWrapperComponent from './kp-video-viewer-event-wrapper.component';

function createVideoElement(src: string) {
    return `<video class="video-js" controls data-setup='{"fluid": true}'>
                <source src="${src}" type="video/mp4">
            </video>`;
}

export class KpVideoViewerComponentController implements KpVideoViewerComponentBindings, OnChanges<KpVideoViewerComponentBindings>, IOnInit, IOnDestroy {
    private static videojsLibrary;
    public src: string;
    public aspectRatio: KpVideoViewerAspectRatio;
    public wrapperComponentController: KpVideoViewerEventWrapperComponentController;
    private videoJSInstance: VideoJsPlayer;

    /*@ngInject*/
    constructor(private $element: IAugmentedJQuery) {
    }

    public $onInit(): void {
        if (this.wrapperComponentController) {
            this.wrapperComponentController.registerVideoViewer(this);
        }
    }

    public $onChanges(onChangesObj: OnChangesObject<KpVideoViewerComponentBindings>) {
        if (onChangesObj.src && onChangesObj.src.currentValue) {
            this.setUrl(onChangesObj.src.currentValue);
        }

        if (onChangesObj.aspectRatio && onChangesObj.aspectRatio.currentValue) {
            this.setAspectRatio(onChangesObj.aspectRatio.currentValue);
        }
    }

    public $onDestroy(): void {
        this.videoJSInstance.dispose();
    }

    public setAspectRatio(aspectRatio: KpVideoViewerAspectRatio) {
        if (!this.videoJSInstance) {
            return;
        }

        this.videoJSInstance.aspectRatio(aspectRatio);
    }

    public async setUrl(url: string) {
        // eslint-disable-next-line no-unused-expressions,@typescript-eslint/no-unused-expressions
        this.videoJSInstance && this.videoJSInstance.dispose();

        const videojs = await this.loadVideoJSLibrary();

        this.$element.append(createVideoElement(url));
        const element = this.$element[0].querySelector('video');
        this.videoJSInstance = videojs(element);
    }

    private async loadVideoJSLibrary() {
        if (!KpVideoViewerComponentController.videojsLibrary) {
            const {default: videojs} = await import(/* webpackChunkName: "videojs-dist" */ 'video.js');
            KpVideoViewerComponentController.videojsLibrary = videojs;
        }

        const loadingElement = this.$element[0].querySelector('jp-loading');
        if (loadingElement) {
            loadingElement.remove();
        }

        return KpVideoViewerComponentController.videojsLibrary;
    }

}

/**
 * @ngdoc component
 * @name kpVideoViewer
 * @module portaro.components
 *
 * @param {string} src Source URL.
 * @param {KpVideoViewerAspectRatio} aspectRatio Player aspect ratio.
 *
 * @description
 *
 */
export default class KpVideoViewerComponent implements Component<KpVideoViewerComponentBindings> {
    public static componentName = 'kpVideoViewer';

    public template = '<jp-loading></jp-loading>';

    public bindings = {
        src: '<',
        aspectRatio: '<'
    } as const;
    public controller = KpVideoViewerComponentController;
    public require = {
        wrapperComponentController: `^^?${KpVideoViewerEventWrapperComponent.componentName}`
    };
}

interface KpVideoViewerComponentBindings {
    src: string;
    aspectRatio: KpVideoViewerAspectRatio;
}

export enum KpVideoViewerAspectRatio {
    A4_3 = '4:3',
    A16_9 = '16:9'
}
